import { useState, useEffect, useCallback } from 'react';
import { Car, FileText, Calendar, Wrench, AlertCircle, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import VehicleTimeline from '../components/timeline/VehicleTimeline';
import { supabase } from '../lib/supabase';
import { loadOfficePlanFeatures } from '../lib/planFeatures';
import { currency, date } from '../utils/formatters';

export default function CustomerPortal() {
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [planFeatures, setPlanFeatures] = useState({});
  const [activeTab, setActiveTab] = useState('history');

  const loadCustomerData = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const clienteId = user.user_metadata?.cliente_id;
      if (!clienteId) throw new Error('Cliente não vinculado');

      const { data: veiculos, error: veiculosError } = await supabase
        .from('veiculos')
        .select('*')
        .eq('cliente_id', clienteId);

      if (veiculosError) throw veiculosError;
      setVehicles(veiculos || []);

      if (veiculos && veiculos.length > 0) {
        setSelectedVehicle(veiculos[0]);
        const plan = await loadOfficePlanFeatures(veiculos[0].oficina_id);
        setPlanFeatures(plan.features);
        await loadServiceOrders(veiculos[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomerData();
  }, [loadCustomerData]);

  const loadServiceOrders = async (veiculoId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const clienteId = user.user_metadata?.cliente_id;

      const { data: ordens, error } = await supabase
        .from('ordens_servico')
        .select(`
          *,
          clientes!inner(nome),
          veiculos!inner(marca, modelo, placa)
        `)
        .eq('cliente_id', clienteId)
        .eq('veiculo_id', veiculoId)
        .order('data_entrada', { ascending: false });

      if (error) throw error;
      setServiceOrders(ordens || []);
    } catch (error) {
      console.error('Erro ao carregar ordens:', error);
    }
  };

  const handleVehicleChange = (veiculo) => {
    setSelectedVehicle(veiculo);
    loadServiceOrders(veiculo.id);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-900 to-zinc-800">
        <div className="text-center text-white">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-orange-500 border-t-transparent mx-auto" />
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 to-zinc-800">
      <nav className="border-b border-white/10 bg-white/5 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500">
                <Car size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">MecSync</h1>
                <p className="text-xs text-zinc-400">Portal do Cliente</p>
              </div>
            </div>
            <Button variant="ghost" onClick={handleLogout} className="text-zinc-400 hover:text-white">
              <LogOut size={16} className="mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </nav>

      <div className="mx-auto max-w-7xl px-4 py-8">
        <div className="mb-8">
          <h2 className="mb-2 text-3xl font-bold text-white">Bem-vindo ao Portal</h2>
          <p className="text-zinc-400">Acompanhe seus veículos e histórico de serviços</p>
        </div>

        {vehicles.length === 0 ? (
          <Card>
            <div className="flex flex-col items-center py-12 text-center">
              <AlertCircle size={48} className="mb-4 text-zinc-500" />
              <h3 className="mb-2 text-lg font-semibold text-white">Nenhum veículo encontrado</h3>
              <p className="text-zinc-400">
                Entre em contato com a oficina para vincular seus veículos.
              </p>
            </div>
          </Card>
        ) : (
          <>
            <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
              {vehicles.map((veiculo) => (
                <button
                  key={veiculo.id}
                  onClick={() => handleVehicleChange(veiculo)}
                  className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition ${
                    selectedVehicle?.id === veiculo.id
                      ? 'border-orange-500 bg-orange-500/10 text-white'
                      : 'border-white/10 bg-white/5 text-zinc-400 hover:border-white/20'
                  }`}
                >
                  <Car size={20} />
                  <div className="text-left">
                    <p className="font-semibold">{veiculo.marca} {veiculo.modelo}</p>
                    <p className="text-xs">{veiculo.placa}</p>
                  </div>
                </button>
              ))}
            </div>

            <div className="mb-6 grid gap-4 md:grid-cols-3">
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/10">
                    <FileText size={20} className="text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Total de Serviços</p>
                    <p className="text-2xl font-bold text-white">{serviceOrders.length}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/10">
                    <Wrench size={20} className="text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Serviços Concluídos</p>
                    <p className="text-2xl font-bold text-white">
                      {serviceOrders.filter((os) => ['finalizada', 'entregue'].includes(os.status)).length}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500/10">
                    <Calendar size={20} className="text-orange-400" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Último Serviço</p>
                    <p className="text-sm font-semibold text-white">
                      {serviceOrders.length > 0
                        ? date(serviceOrders[0].data_entrada)
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            <div className="mb-6">
              <div className="flex gap-2 border-b border-white/10">
                <button
                  onClick={() => setActiveTab('history')}
                  className={`px-4 py-3 font-medium transition ${
                    activeTab === 'history'
                      ? 'border-b-2 border-orange-500 text-white'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  Histórico de Serviços
                </button>
                {planFeatures.timeline && (
                  <button
                    onClick={() => setActiveTab('timeline')}
                    className={`px-4 py-3 font-medium transition ${
                      activeTab === 'timeline'
                        ? 'border-b-2 border-orange-500 text-white'
                        : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    Timeline
                  </button>
                )}
              </div>
            </div>

            {activeTab === 'history' && (
              <Card>
                <div className="space-y-3">
                  {serviceOrders.length === 0 ? (
                    <div className="py-8 text-center text-zinc-500">
                      Nenhum serviço encontrado
                    </div>
                  ) : (
                    serviceOrders.map((os) => (
                      <div
                        key={os.id}
                        className="flex items-start justify-between rounded-lg border border-white/10 bg-white/5 p-4"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <p className="font-semibold text-white">OS #{os.numero}</p>
                            <Badge
                              className={`${
                                os.status === 'finalizada' || os.status === 'entregue'
                                  ? 'bg-green-500/10 text-green-400'
                                  : 'bg-blue-500/10 text-blue-400'
                              }`}
                            >
                              {os.status}
                            </Badge>
                          </div>
                          <p className="mt-1 text-sm text-zinc-400">{os.problema_relatorio || 'Sem descrição'}</p>
                          <p className="mt-1 text-xs text-zinc-500">
                            {date(os.data_entrada)}
                            {os.data_saida && ` - ${date(os.data_saida)}`}
                          </p>
                        </div>
                        {os.valor_total && (
                          <div className="ml-4 text-right">
                            <p className="font-semibold text-white">{currency(os.valor_total)}</p>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </Card>
            )}

            {activeTab === 'timeline' && selectedVehicle && planFeatures.timeline && (
              <VehicleTimeline
                veiculoId={selectedVehicle.id}
                clienteId={selectedVehicle.cliente_id}
                limit={50}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
